<template>
  <div class="admin-dashboard container py-4">
    <div class="skip-links">
      <a href="#main-content" class="skip-link">Zum Hauptinhalt</a>
    </div>

    <div class="container-fluid p-4" id="main-content">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="mb-0">
          <font-awesome-icon icon="cog" class="me-2" />
          Administration
        </h1>
      </div>

      <!-- Admin Dashboard Content -->
      <div class="admin-content">
        <!-- Statistics Section -->
        <div class="row g-4 mb-4">
          <div class="col-12">
            <div class="card shadow-sm">
              <div class="card-body">
                <h2 class="card-title mb-3">
                  <font-awesome-icon icon="chart-bar" class="me-2" />
                  System-Statistiken
                </h2>
                
                <!-- Loading -->
                <div v-if="loading" class="text-center my-5">
                  <div class="spinner-border" role="status">
                    <span class="visually-hidden">Statistiken werden geladen...</span>
                  </div>
                </div>

                <!-- Error -->
                <div v-else-if="error" class="alert alert-danger" role="alert">
                  <font-awesome-icon icon="exclamation-triangle" class="me-2" />
                  {{ error }}
                </div>

                <!-- Statistics -->
                <div v-else class="stats-container">
                  <div class="stats-row">
                    <div class="stat-card stat-card-primary">
                      <div class="stat-card-icon">
                        <font-awesome-icon icon="file-invoice-dollar" />
                      </div>
                      <div class="stat-card-content">
                        <div class="stat-card-number">{{ statistics.combinedTotalBillings || 0 }}</div>
                        <div class="stat-card-label">Gesamt-Rechnungen</div>
                      </div>
                    </div>
                    <div class="stat-card stat-card-success">
                      <div class="stat-card-icon">
                        <font-awesome-icon icon="euro-sign" />
                      </div>
                      <div class="stat-card-content">
                        <div class="stat-card-number">{{ formatCurrency(statistics.combinedTotalRevenue || 0) }}</div>
                        <div class="stat-card-label">Gesamtumsatz</div>
                      </div>
                    </div>
                    <div class="stat-card stat-card-info">
                      <div class="stat-card-icon">
                        <font-awesome-icon icon="chart-line" />
                      </div>
                      <div class="stat-card-content">
                        <div class="stat-card-number">{{ formatCurrency(statistics.averageOrderValue || 0) }}</div>
                        <div class="stat-card-label">Ø Bestellwert</div>
                      </div>
                    </div>
                    <div class="stat-card stat-card-warning">
                      <div class="stat-card-icon">
                        <font-awesome-icon icon="coins" />
                      </div>
                      <div class="stat-card-content">
                        <div class="stat-card-number">{{ formatNumber(statistics.totalCoins || 0) }}</div>
                        <div class="stat-card-label">Gesamt-Quittungen</div>
                      </div>
                    </div>
                    <div class="stat-card stat-card-secondary">
                      <div class="stat-card-icon">
                        <font-awesome-icon icon="receipt" />
                      </div>
                      <div class="stat-card-content">
                        <div class="stat-card-number">{{ formatCurrency(statistics.combinedTotalVAT || 0) }}</div>
                        <div class="stat-card-label">Gesamt-MwSt</div>
                      </div>
                    </div>
                    <div class="stat-card stat-card-primary">
                      <div class="stat-card-icon">
                        <font-awesome-icon icon="euro-sign" />
                      </div>
                      <div class="stat-card-content">
                        <div class="stat-card-number">{{ formatCurrency(statistics.totalRevenue || 0) }}</div>
                        <div class="stat-card-label">Quittung-Umsatz</div>
                      </div>
                    </div>
                    <div class="stat-card stat-card-info">
                      <div class="stat-card-icon">
                        <font-awesome-icon icon="euro-sign" />
                      </div>
                      <div class="stat-card-content">
                        <div class="stat-card-number">{{ formatCurrency(statistics.totalTeamRevenue || 0) }}</div>
                        <div class="stat-card-label">Bereich-Umsatz</div>
                      </div>
                    </div>
                    <div class="stat-card stat-card-success">
                      <div class="stat-card-icon">
                        <font-awesome-icon icon="users" />
                      </div>
                      <div class="stat-card-content">
                        <div class="stat-card-number">{{ statistics.totalTeamBillings || 0 }}</div>
                        <div class="stat-card-label">Bereich-Upgrades</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Admin Cards Section -->
        <div class="row g-4">
          <div class="col-12">
            <div class="card shadow-sm">
              <div class="card-body">
                <h2 class="card-title mb-3">
                  <font-awesome-icon icon="tachometer-alt" class="me-2" />
                  Dashboard Übersicht
                </h2>
                <p class="text-muted mb-4">
                  Willkommen im Admin-Bereich. Hier kannst du verschiedene Systemeinstellungen verwalten.
                </p>
                
                <div class="row g-3">
                  <div class="col-md-6 col-lg-4">
                    <router-link 
                      to="/admin/salesperson"
                      class="admin-card-link"
                    >
                      <div class="admin-card">
                        <div class="admin-card-icon">
                          <font-awesome-icon icon="building" />
                        </div>
                        <div class="admin-card-content">
                          <h5>Verkäufer-Daten</h5>
                          <p class="text-muted small">Verwalte deine Unternehmensdaten</p>
                        </div>
                      </div>
                    </router-link>
                  </div>
                  
                  <div class="col-md-6 col-lg-4">
                    <router-link 
                      to="/admin/global-settings"
                      class="admin-card-link"
                    >
                      <div class="admin-card">
                        <div class="admin-card-icon">
                          <font-awesome-icon icon="cogs" />
                        </div>
                        <div class="admin-card-content">
                          <h5>Globale Einstellungen</h5>
                          <p class="text-muted small">Wartungsmodus und System-Konfiguration</p>
                        </div>
                      </div>
                    </router-link>
                  </div>
                  
                  <div class="col-md-6 col-lg-4">
                    <router-link 
                      to="/admin/billings"
                      class="admin-card-link"
                    >
                      <div class="admin-card">
                        <div class="admin-card-icon">
                          <font-awesome-icon icon="file-invoice-dollar" />
                        </div>
                        <div class="admin-card-content">
                          <h5>Rechnungs-Verwaltung</h5>
                          <p class="text-muted small">Alle Rechnungen einsehen und verwalten</p>
                        </div>
                      </div>
                    </router-link>
                  </div>
                  

                  <div class="col-md-6 col-lg-4">
                    <router-link 
                      to="/admin/pricing"
                      class="admin-card-link"
                    >
                      <div class="admin-card">
                        <div class="admin-card-icon">
                          <font-awesome-icon icon="euro-sign" />
                        </div>
                        <div class="admin-card-content">
                          <h5>Preis-Einstellungen</h5>
                          <p class="text-muted small">Regeln für Quittungen & Bereich-Pläne verwalten</p>
                        </div>
                      </div>
                    </router-link>
                  </div>

                  <div class="col-md-6 col-lg-4">
                    <router-link 
                      to="/admin/apple-transactions"
                      class="admin-card-link"
                    >
                      <div class="admin-card">
                        <div class="admin-card-icon">
                          <font-awesome-icon icon="apple-alt" />
                        </div>
                        <div class="admin-card-content">
                          <h5>Apple In‑App Käufe</h5>
                          <p class="text-muted small">Alle Apple-Transaktionen mit Details</p>
                        </div>
                      </div>
                    </router-link>
                  </div>

                  <div class="col-md-6 col-lg-4">
                    <router-link 
                      to="/admin/stores"
                      class="admin-card-link"
                    >
                      <div class="admin-card">
                        <div class="admin-card-icon">
                          <font-awesome-icon icon="store" />
                        </div>
                        <div class="admin-card-content">
                          <h5>Stores</h5>
                          <p class="text-muted small">Google & Apple Abrechnungen verwalten</p>
                        </div>
                      </div>
                    </router-link>
                  </div>

                  <div class="col-md-6 col-lg-4">
                    <router-link 
                      to="/admin/coupons"
                      class="admin-card-link"
                    >
                      <div class="admin-card">
                        <div class="admin-card-icon">
                          <font-awesome-icon icon="ticket-alt" />
                        </div>
                        <div class="admin-card-content">
                          <h5>Coupon-Verwaltung</h5>
                          <p class="text-muted small">Gutscheine erstellen und verwalten</p>
                        </div>
                      </div>
                    </router-link>
                  </div>

                  <div class="col-md-6 col-lg-4">
                    <router-link 
                      to="/admin/users"
                      class="admin-card-link"
                    >
                      <div class="admin-card">
                        <div class="admin-card-icon">
                          <font-awesome-icon icon="users" />
                        </div>
                        <div class="admin-card-content">
                          <h5>Benutzerverwaltung</h5>
                          <p class="text-muted small">Alle Benutzer einsehen und verwalten</p>
                        </div>
                      </div>
                    </router-link>
                  </div>

                  <div class="col-md-6 col-lg-4">
                    <router-link 
                      to="/admin/templates"
                      class="admin-card-link"
                    >
                      <div class="admin-card">
                        <div class="admin-card-icon">
                          <font-awesome-icon icon="file-alt" />
                        </div>
                        <div class="admin-card-content">
                          <h5>Template-Verwaltung</h5>
                          <p class="text-muted small">Quittungsvorlagen erstellen und verwalten</p>
                        </div>
                      </div>
                    </router-link>
                  </div>

                  <div class="col-md-6 col-lg-4">
                    <router-link 
                      to="/admin/teams"
                      class="admin-card-link"
                    >
                      <div class="admin-card">
                        <div class="admin-card-icon">
                          <font-awesome-icon icon="users" />
                        </div>
                        <div class="admin-card-content">
                          <h5>Bereiche-Übersicht</h5>
                          <p class="text-muted small">Alle Bereiche mit Status und Kontostand verwalten</p>
                        </div>
                      </div>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { adminApi } from '@/api/admin'


const loading = ref(false)
const error = ref(null)
const statistics = ref({
  totalBillings: 0,
  totalRevenue: 0,
  totalCoins: 0,
  totalVAT: 0,
  totalTeamBillings: 0,
  totalTeamRevenue: 0,
  totalTeamVAT: 0,
  totalSystemPurchases: 0,
  totalSystemPurchaseCoins: 0,
  combinedTotalRevenue: 0,
  combinedTotalVAT: 0,
  combinedTotalBillings: 0,
  averageOrderValue: 0
})


const loadStatistics = async () => {
  loading.value = true
  error.value = null
  
  try {
    const stats = await adminApi.getBillingStats()
    statistics.value = {
      ...stats,

      totalSystemPurchases: stats.totalSystemPurchases || 0,
      totalSystemPurchaseCoins: stats.totalSystemPurchaseCoins || 0,
      combinedTotalRevenue: stats.combinedTotalRevenue || 0,
      averageOrderValue: stats.averageOrderValue || 0
    }
  } catch (err) {
    console.error('Error loading statistics:', err)
    error.value = err.response?.data?.message || 'Fehler beim Laden der Statistiken'
  } finally {
    loading.value = false
  }
}

const formatCurrency = (amount) => {
  if (!amount) return '€0,00'
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount)
}

const formatNumber = (number) => {
  if (!number) return '0'
  return new Intl.NumberFormat('de-DE').format(number)
}


onMounted(() => {
  loadStatistics()
})
</script>
